import {
  Backdrop,
  Button,
  Grid,
  Modal,
  TextField,
  TextFieldProps,
  Typography,
} from '@material-ui/core';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import { Autocomplete } from '@material-ui/lab';
import ButtonWithLoading from 'components/atoms/ButtonWithLoading';
import ConfirmFinishModal from 'components/atoms/ConfirmFinishModal';
import ConfirmModal from 'components/atoms/ConfirmModal';
import LoadingInputEndAdornment from 'components/atoms/LoadingInputEndAdornment';
import FollowUpReport from 'components/molecules/FollowUpReport';
import { IOption } from 'components/molecules/RegisterInspectionChecklist/Components/FormRegisterChecklist/types';
import dayjs from 'dayjs';
import { useFormik } from 'formik';
import { useAuth } from 'hooks/useAuth';
import { useUploadFile } from 'hooks/upload/useUploadFile';
import { IEquipmentFilterTree } from 'interfaces/Equipment';
import {
  IAddFollowUp,
  IFollowUpReport,
  IForm,
  IFormFollowUp,
} from 'interfaces/FollowUpServices';
import { IFilterOptions } from 'interfaces/SelectOptions';
import React from 'react';
import { useQuery } from 'react-query';
import { getCluster } from 'services/cluster';
import {
  getFollowUpService,
  postAddFollowUpService,
  putFollowUpService,
} from 'services/follow-up';
import { getInstallationFilter } from 'services/installation';
import * as Yup from 'yup';
import FormFollowUp from './FormFollowUp';
import { IFormAddFollowUpRef } from './FormFollowUp/types';
import useStyles from './style';
import { IEquipmentType, IFollowUpModalAddProps } from './types';

const MAX_AMOUNT_FORM = 15;
const DEFAULT_ERROR_TEXT = 'Preenchimento inválido';
const DEFAULT_HELPER_TEXT = 'Obrigatório';
const NO_OPTIONS_TEXT = 'Nenhum resultado encontrado';
const ERROR_TEXT100 = 'Texto ultrapassou o limite de 100 caracteres';
const ERROR_TEXT450 = 'Texto ultrapassou o limite de 450 caracteres';

const getNewDraftForm = () => {
  return {
    order: 0,
    date: new Date().toISOString(),
    equipmentId: '',
    equipmentDescription: '',
    provider: [],
    description: '',
    attachment: [],
    tempAttachments: [],
    followUpFormId: Math.random(),
  } as IFormFollowUp;
};

const INITIAL_VALUES = {
  title: '',
  installationId: 0,
  form: [getNewDraftForm()],
} as IAddFollowUp;

const mapFilterOptions = (
  data: {
    id: number;
    description: string;
  }[],
) =>
  data.map(item => {
    return {
      label: item?.description,
      value: item?.id,
    };
  }) as IOption[];

const FollowUpModalAdd = ({
  open,
  onClose,
  children,
  reloadData,
  isEditing,
  gridRow,
}: IFollowUpModalAddProps) => {
  const classes = useStyles();
  const { account } = useAuth();

  const formFollowUpRefs = React.useRef<
    Record<string, IFormAddFollowUpRef | null>
  >({});
  const [saveFormLoading, setSaveFormLoading] = React.useState<boolean>(false);
  const [renderForm, setRenderForm] = React.useState<boolean>(true);
  const [openConfirmModal, setOpenConfirmModal] = React.useState(false);
  const [openFinishModal, setOpenFinishModal] = React.useState<boolean>(false);
  const enableQueries = React.useMemo(() => open, [open]);
  const [installationOptions, setInstallationOptions] = React.useState<
    IOption[]
  >([]);
  const [filterClusterOptions, setFilterClusterOptions] = React.useState<
    IFilterOptions[]
  >([]);
  const [installationValue, setInstallationValue] =
    React.useState<IOption | null>(null);

  const [equipmentValues, setEquipmentValues] = React.useState<IEquipmentType>(
    {},
  );

  const { mutateAsync: uploadFollowup, isError: isUploadError } =
    useUploadFile();

  const validationSchema = Yup.object({
    installationId: Yup.number()
      .min(1, DEFAULT_HELPER_TEXT)
      .required(DEFAULT_HELPER_TEXT),
    title: Yup.string().max(100, ERROR_TEXT100).required(DEFAULT_HELPER_TEXT),
    form: Yup.array()
      .of(
        Yup.object({
          followUpFormId: Yup.number().notRequired(),
          order: Yup.number().required(DEFAULT_HELPER_TEXT),
          date: Yup.mixed()
            .required(DEFAULT_HELPER_TEXT)
            .nullable()
            .test('is-valid', DEFAULT_ERROR_TEXT, value => {
              return dayjs(value).isValid();
            })
            .typeError(DEFAULT_ERROR_TEXT),
          equipmentId: Yup.number().notRequired(),
          attachment: Yup.array().of(Yup.mixed().notRequired()),
          description: Yup.string().max(450, ERROR_TEXT450),
        }).test(
          'is-description-or-attachment',
          ({ attachment, description }) => {
            return Boolean(
              (Array.isArray(attachment) && attachment.length) ||
                description?.trim().length,
            );
          },
        ),
      )
      .min(1)
      .max(MAX_AMOUNT_FORM)
      .test('is-valid-unique', DEFAULT_ERROR_TEXT, value => {
        if (!value || !value) {
          return true;
        }

        const orderList = value.map(item => item.order);
        const newList = new Set(orderList);
        return orderList.length === newList.size;
      }),
  });

  const shortName = React.useMemo(() => {
    if (!account?.username) return null;
    return account?.username.split('@')[0];
  }, [account]);

  const removePhotosDuplicates = (photos: File[]): File[] => {
    const uniqueFiles = new Map<string, File>();

    photos.forEach(photo => {
      const fileKey = `${photo.name}-${photo.size}`;
      if (!uniqueFiles.has(fileKey)) {
        uniqueFiles.set(fileKey, photo);
      }
    });

    return Array.from(uniqueFiles.values());
  };

  const renamePhotoWithHashPrefix = (photos: File) => {
    const hash = Math.random().toString(36).substring(7);
    const splitedName = photos.name.split('.');
    const extension = splitedName[splitedName.length - 1];
    const newName = `${hash}_${photos.name}`;
    return new File([photos], newName, { type: `image/${extension}` });
  };

  const formik = useFormik<IAddFollowUp>({
    enableReinitialize: true,
    initialValues: INITIAL_VALUES,
    isInitialValid: false,
    validationSchema,
    onSubmit: async formValues => {
      setSaveFormLoading(true);
      const form = formValues.form.map(
        async ({
          equipmentId,
          attachment,
          date,
          description,
          provider,
          followUpFormId,
          order,
        }) => {
          const uploadAttachments = (files: File[]) => {
            const uniqueFiles = removePhotosDuplicates(files);
            return Promise.all(
              uniqueFiles.map(async file => {
                const renamedFile = renamePhotoWithHashPrefix(file);
                if (
                  (file as File & { preview?: string })?.preview &&
                  (file as File & { isRemote?: boolean }).isRemote
                ) {
                  const { preview } = file as File & {
                    preview?: string;
                  };

                  return preview ? preview.split('?')[0] : '';
                }

                const params = {
                  path: renamedFile.name,
                  file: renamedFile,
                };

                return uploadFollowup(params);
              }),
            );
          };

          const attachments = await uploadAttachments(attachment as File[]);

          return {
            followUpFormId: Number.isInteger(followUpFormId)
              ? followUpFormId
              : undefined,
            equipmentId: equipmentId === '' ? null : equipmentId,
            attachment: attachments,
            date,
            description,
            provider,
            orderNumber: order,
          };
        },
      );

      const { title, installationId } = formValues;

      try {
        const params = {
          title,
          installationId,
          form: await Promise.all(form),
        };

        if (!isEditing && !isUploadError) {
          await postAddFollowUpService(params);

          if (reloadData) reloadData();
          onClose();
          setRenderForm(true);
        }

        if (isEditing) {
          const followUpId = gridRow?.[0].id;
          await putFollowUpService(followUpId, params);
          if (reloadData) reloadData();
          onClose();
          setRenderForm(true);
        }
      } finally {
        setSaveFormLoading(false);
      }
    },
  });

  const followUpInfo = React.useMemo<IFollowUpReport>(() => {
    const arrayTimeStamp = formik.values.form.map(x =>
      new Date(x.date).getTime(),
    );
    const startDate = new Date(Math.min(...arrayTimeStamp)).toISOString();
    const endDate = new Date(Math.max(...arrayTimeStamp)).toISOString();
    return {
      title: formik.values.title,
      createdUser: shortName as string,
      createdDate: dayjs().format('MM/DD/YYYY'),
      startDate,
      endDate,
      id: 0,
      cluster:
        filterClusterOptions.find(clusterOptions => {
          return (clusterOptions?.children || []).some(
            installation =>
              `${installation.value}` === `${formik.values.installationId}`,
          );
        })?.label || '',
      installationCode:
        installationOptions.find(
          installation => installation.value === formik.values.installationId,
        )?.label || '',
      forms: formik.values.form.map((form, index) => ({
        description: form.description,
        equipmentDescription: equipmentValues[index]?.description || '',
        provider: form.provider,
        images: form.attachment.map(file =>
          file?.isRemote ? file?.preview : URL.createObjectURL(file),
        ),
        date: form.date,
        order: form.order,
        tempImages: form.tempAttachments,
      })) as IForm[],
    };
  }, [
    equipmentValues,
    filterClusterOptions,
    formik.values.form,
    formik.values.installationId,
    formik.values.title,
    installationOptions,
    shortName,
  ]);

  const onCloseModal = React.useCallback(() => {
    if (formik.dirty) {
      setOpenConfirmModal(true);
    } else {
      onClose();
      setRenderForm(true);
    }
  }, [formik.dirty, onClose]);

  const handleAutoCompleteChange = React.useCallback(
    (name: string, option: IOption) => {
      const value = option?.value || '';
      formik.setFieldValue(name, value);
    },
    [formik],
  );

  const getOptionSelected = (option: IOption, anotherOption: IOption) =>
    option.value === anotherOption.value;

  const inputRenderer = React.useCallback(
    (params: TextFieldProps, loading?: boolean) => (
      <TextField
        {...params}
        className={classes.formBaseInput}
        variant="outlined"
        InputProps={{
          ...params.InputProps,
          endAdornment: (
            <>
              <LoadingInputEndAdornment loading={loading} />
              {params?.InputProps?.endAdornment}
            </>
          ),
        }}
      />
    ),
    [classes.formBaseInput],
  );

  const enableInstallationOptions = React.useMemo(
    () => enableQueries,
    [enableQueries],
  );

  const enableClusterOptions = React.useMemo(
    () => enableQueries,
    [enableQueries],
  );

  const enableQueryUpdateFollowUp = React.useMemo(() => {
    return enableQueries && Boolean(gridRow?.[0].id);
  }, [enableQueries, gridRow]);

  const queryInstallationOptions = useQuery({
    queryKey: ['installationOptionsContext'],
    queryFn: async () => {
      const { data } = await getInstallationFilter();
      return data;
    },
    select: data => {
      return mapFilterOptions(data);
    },
    onSuccess: (data: IOption[]) => {
      setInstallationOptions(data);
    },
    enabled: enableInstallationOptions,
  });

  useQuery({
    queryKey: ['clusterFilterOptionsContext'],
    queryFn: async () => {
      const { data } = await getCluster();
      return data;
    },
    select: data => {
      return data.map(item => {
        return {
          label: item?.description,
          value: item?.id,
          children: item.installations.map(installation => {
            return {
              label: installation?.description,
              value: installation?.id,
            };
          }),
        };
      });
    },
    onSuccess: (data: IFilterOptions[]) => {
      setFilterClusterOptions(data);
    },
    enabled: enableClusterOptions,
  });

  useQuery({
    queryKey: ['UpdateFollowUpServiceContext'],
    queryFn: async () => {
      const followUpId = gridRow?.[0].id;
      const { data } = await getFollowUpService(followUpId);
      return data;
    },
    select: (data: IAddFollowUp) => {
      const updateFollowUpService = {
        title: data.title,
        installationId: data.installationId,
        cluster: data.cluster,
        form: data.form.map(
          formFollowUp =>
            ({
              followUpFormId: formFollowUp.followUpFormId,
              order: formFollowUp.order,
              date: formFollowUp.date,
              equipmentId: formFollowUp.equipmentId,
              equipmentDescription: formFollowUp.equipmentDescription,
              provider: formFollowUp.provider,
              description: formFollowUp.description,
              attachment: formFollowUp.attachment,
              tempAttachments: formFollowUp.tempAttachments,
            } as IFormFollowUp),
        ),
      };

      return updateFollowUpService;
    },
    onSuccess: (data: IAddFollowUp) => {
      if (data) {
        const installationOption = installationOptions.find(
          item => item.value === data.installationId,
        );
        if (installationOption) {
          setInstallationValue(installationOption);
        }

        const formEquipment = {} as IEquipmentType;
        data.form.forEach((formValues, index) => {
          formEquipment[index] = {
            description: formValues.equipmentDescription || null,
            id: formValues.equipmentId || null,
          } as IEquipmentFilterTree;
        });
        if (installationOption) {
          setEquipmentValues(formEquipment);
        }

        formik.setValues({
          ...data,
          form: data.form.map(
            formFollowUp =>
              ({
                followUpFormId: formFollowUp.followUpFormId,
                order: formFollowUp.order,
                date: formFollowUp.date,
                equipmentId: formFollowUp.equipmentId,
                equipmentDescription: formFollowUp.equipmentDescription,
                provider: formFollowUp.provider,
                description: formFollowUp.description,
                attachment: formFollowUp.attachment
                  .map((fileUrl: string | File) => {
                    if (typeof fileUrl === 'string') {
                      const splitedUrl = fileUrl.split('/');
                      const fileData = splitedUrl[splitedUrl.length - 1];
                      const splitedFile = fileData.split('.');
                      const name = splitedFile[0].split('_');
                      const extension = splitedFile[splitedFile.length - 1];
                      let fullName = `${name[1]}.${extension}`;
                      const normalizedName = decodeURI(fullName);
                      if (fullName !== normalizedName)
                        fullName = normalizedName;

                      const file = new File([], fullName, {
                        type: `image/${extension}`,
                      });

                      let assignedUrl: string | undefined = '';
                      if (formFollowUp.tempAttachments) {
                        assignedUrl = formFollowUp.tempAttachments.find(
                          tempAttachment =>
                            tempAttachment.indexOf(fileData) !== -1,
                        );
                      }

                      return Object.assign(file, {
                        preview: assignedUrl || fileUrl,
                        isRemote: true,
                      });
                    }
                    return null;
                  })
                  .filter(file => Boolean(file)),
              } as IFormFollowUp),
          ),
        });
      }
    },
    cacheTime: 0,
    enabled: enableQueryUpdateFollowUp,
  });

  const disableAddFormButton = React.useMemo(() => {
    return formik.values.form.length >= MAX_AMOUNT_FORM;
  }, [formik.values.form.length]);

  const canAddForm = React.useMemo(
    () => !disableAddFormButton,
    [disableAddFormButton],
  );

  const deleteForm = React.useCallback(
    (formIndex: number) => () => {
      formik.setValues(currentValues => ({
        ...currentValues,
        form: [
          ...currentValues.form.slice(0, formIndex),
          ...currentValues.form.slice(formIndex + 1),
        ],
      }));
    },
    [formik],
  );

  const addNewForm = React.useCallback(() => {
    if (canAddForm) {
      const draftForm = getNewDraftForm();

      formik.setValues(currentForm => ({
        ...currentForm,
        form: [...currentForm.form, draftForm],
      }));
    }
  }, [canAddForm, formik]);

  const cleanAllEquipmentFields = React.useCallback(() => {
    Object.values(formFollowUpRefs.current)
      .filter(formFollowUpRef => Boolean(formFollowUpRef))
      .forEach(formFollowUpRef => formFollowUpRef?.clearEquipmentField());
  }, []);

  React.useEffect(() => {
    if (!open) {
      setInstallationValue(null);
      if (formik.dirty) {
        formik.resetForm();
      }
    }
  }, [formik, open]);

  const renderHeader = React.useCallback(() => {
    if (renderForm) {
      return (
        <div className={classes.modalHeaderItem}>
          <ArrowBackIcon
            className={classes.headerIcon}
            onClick={onCloseModal}
          />
          <Typography variant="h3" className={classes.headerTitle}>
            {!isEditing ? 'Cadastrar acompanhamento' : 'Editar acompanhamento'}
          </Typography>
        </div>
      );
    }

    return (
      <div className={classes.modalHeaderItem}>
        <ArrowBackIcon
          className={classes.headerIcon}
          onClick={() => setRenderForm(true)}
        />
        <Typography variant="h3" className={classes.headerTitle}>
          Relatório
        </Typography>
      </div>
    );
  }, [
    classes.headerIcon,
    classes.headerTitle,
    classes.modalHeaderItem,
    isEditing,
    onCloseModal,
    renderForm,
  ]);

  const renderBody = React.useCallback(() => {
    if (renderForm) {
      return (
        <form className={classes.bodyContent}>
          <Grid container direction="row" spacing={2}>
            <Grid item xs={6}>
              <TextField
                id="title"
                name="title"
                label="Título"
                variant="outlined"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.title}
                error={formik.touched.title && Boolean(formik.errors.title)}
                helperText={
                  (formik.touched.title && formik.errors.title) ||
                  DEFAULT_HELPER_TEXT
                }
              />
            </Grid>
            <Grid item xs={6}>
              <Autocomplete
                id="installationId"
                className={classes.inputList}
                options={installationOptions}
                value={installationValue}
                onChange={(e, selected) => {
                  setInstallationValue(selected);
                  formik.setValues(oldValues => ({
                    ...oldValues,
                    form: [
                      ...oldValues.form.map(formItem => {
                        return {
                          ...formItem,
                          equipmentId: '',
                        };
                      }),
                    ],
                  }));

                  setEquipmentValues({});
                  cleanAllEquipmentFields();

                  handleAutoCompleteChange(
                    'installationId',
                    selected as IOption,
                  );
                }}
                getOptionLabel={option => String(option.label)}
                getOptionSelected={getOptionSelected}
                noOptionsText={NO_OPTIONS_TEXT}
                onBlur={formik.handleBlur}
                renderInput={params => {
                  const extraProps = {
                    name: 'installationId',
                    label: 'Instalação',
                    error:
                      formik.touched.installationId &&
                      Boolean(formik.errors.installationId),
                    helperText:
                      (formik.touched.installationId &&
                        formik.errors.installationId) ||
                      DEFAULT_HELPER_TEXT,
                  };
                  return inputRenderer(
                    { ...params, ...extraProps },
                    queryInstallationOptions?.isLoading,
                  );
                }}
              />
            </Grid>

            {formik.values.form.map((form, index) => {
              return (
                <FormFollowUp
                  ref={elementRef => {
                    formFollowUpRefs.current[`followup-${index}`] = elementRef;
                  }}
                  key={form.followUpFormId}
                  open={open}
                  formIndex={index}
                  formik={formik}
                  form={form}
                  onDelete={deleteForm(index)}
                  equipmentValues={equipmentValues}
                  setEquipmentValues={setEquipmentValues}
                />
              );
            })}
            <Button
              disabled={disableAddFormButton}
              onClick={addNewForm}
              color="primary"
            >
              Adicionar
            </Button>
          </Grid>
        </form>
      );
    }

    return <FollowUpReport {...followUpInfo} isLoading={false} />;
  }, [
    renderForm,
    followUpInfo,
    classes.bodyContent,
    classes.inputList,
    formik,
    installationOptions,
    installationValue,
    disableAddFormButton,
    addNewForm,
    cleanAllEquipmentFields,
    handleAutoCompleteChange,
    inputRenderer,
    queryInstallationOptions?.isLoading,
    open,
    deleteForm,
    equipmentValues,
  ]);

  return (
    <>
      {children}
      <ConfirmModal
        title="Confirmar Saída"
        message="É possível que as alterações feitas não sejam salvas."
        open={openConfirmModal}
        width="433px"
        onAccept={() => {
          onClose();
          setOpenConfirmModal(false);
          setRenderForm(true);
        }}
        onDecline={() => setOpenConfirmModal(false)}
      />
      <Modal
        open={open}
        onClose={onCloseModal}
        BackdropComponent={props => <Backdrop {...props} invisible />}
        className={classes.presentation}
      >
        <div className={classes.modalPresentation}>
          <div className={classes.modalCard}>
            <div className={classes.modalHeader}>
              {renderHeader()}

              <div className={classes.modalHeaderItem}>
                <ConfirmFinishModal
                  title="Finalizar"
                  message="Você tem certeza que não deseja adicionar mais nenhum item?"
                  open={openFinishModal}
                  width="433px"
                  onAccept={() => {
                    setRenderForm(false);
                    setOpenFinishModal(false);
                  }}
                  onDecline={() => setOpenFinishModal(false)}
                />
                {renderForm ? (
                  <Button
                    color="primary"
                    variant="contained"
                    disabled={!formik.isValid}
                    onClick={() => {
                      if (formik.values.form.length === MAX_AMOUNT_FORM) {
                        setRenderForm(false);
                      } else {
                        setOpenFinishModal(true);
                      }
                    }}
                  >
                    Finalizar
                  </Button>
                ) : (
                  <ButtonWithLoading
                    color="primary"
                    variant="contained"
                    disabled={!formik.isValid}
                    isLoading={saveFormLoading}
                    onClick={() => {
                      formik.submitForm();
                    }}
                  >
                    Confirmar
                  </ButtonWithLoading>
                )}
              </div>
            </div>
            <div className={classes.modalBody}>{renderBody()}</div>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default FollowUpModalAdd;
